import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuoteEditorApi } from '../../hooks/use-quote-editor.ts';
import { Alert, Checkbox, Select } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { localizedText, parseLocalizedText } from '@shared/constants/localized-text.ts';
import { firstCleanOptionName, type FirstCleanOption } from '@shared/first-clean-state.ts';
import { quoteFirstCleanPrice, type FirstCleanPrice } from '@shared/first-clean-price.ts';
import type { Quote } from '@shared/types/index.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { firstVisitCents } from './plain-price.tsx';

export function ScopeOption({ option, currency }: { option: FirstCleanOption; currency: string }) {
  const { t, i18n } = useTranslation();
  return (
    <article className="mb-4">
      <h3 className="font-semibold">{firstCleanOptionName(option, i18n.language.startsWith('es') ? 'es' : 'en')}</h3>
      {(
        [
          ['firstVisit', 'firstPrice'],
          ['routine', 'routinePrice'],
        ] as const
      ).map(([key, label]) => (
        <div key={key}>
          <p>
            <strong>
              {t(`firstClean.${label}`)}: {formatCents(option[key].pricing.total_cents, currency, getDateLocale())}
            </strong>
          </p>
          <ul>
            {option[key].tasks.map((task, i) => (
              <li key={i}>
                {localizedText(
                  task.areaName,
                  parseLocalizedText(task.areaNameI18n),
                  i18n.language.startsWith('es') ? 'es' : 'en',
                )}
                : {task.quantity > 1 ? `${task.quantity} × ` : ''}
                {localizedText(
                  task.taskName,
                  parseLocalizedText(task.taskNameI18n),
                  i18n.language.startsWith('es') ? 'es' : 'en',
                )}
                {task.description
                  ? ` — ${localizedText(task.description, parseLocalizedText(task.descriptionI18n), i18n.language.startsWith('es') ? 'es' : 'en')}`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </article>
  );
}
/** The quote with its first clean price set, or turned off. */
export function withFirstCleanPrice(draft: Quote, next: FirstCleanPrice | null): Quote {
  return {
    ...draft,
    firstCleanPrice: next,
    // Turning it off makes every task a regular one again, rather than
    // leaving marks behind that would come back with the next switch on.
    ...(next === null
      ? {
          rooms: draft.rooms.map((room) => ({
            ...room,
            services: room.services.map((task) => (task.firstVisitOnly ? { ...task, firstVisitOnly: false } : task)),
          })),
        }
      : {}),
  };
}

/**
 * The Rooms step's switch for "First visit only" on each task.
 *
 * The same setting as "Price the first clean separately" on the Send step:
 * on, it starts the first clean with no extra, so only the ticked tasks set it
 * apart; off, it clears the first clean price and every tick with it.
 */
export function FirstVisitTasksToggle({
  quote,
  update,
  readOnly,
}: {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  if (quote.billingBasis !== 'per_visit' || quote.acceptedFirstClean) return null;
  const on = quoteFirstCleanPrice(quote) !== null;
  return (
    <section className="card mb-6">
      <Checkbox
        label={t('firstClean.showTaskOption')}
        hint={t('firstClean.showTaskOptionHint')}
        checked={on}
        disabled={readOnly}
        onChange={(event) =>
          update((draft) => withFirstCleanPrice(draft, event.target.checked ? { kind: 'fixed', value: 0 } : null))
        }
      />
    </section>
  );
}

/**
 * The first clean, on the quote itself.
 *
 * A recurring per-visit quote can charge its first visit more: a set amount or
 * a percent on top of a regular visit, and any task marked "First visit only"
 * on the Rooms step. The Worker builds the offer the customer sees from the
 * quote on every save, so there is nothing else to prepare or approve here.
 */
export function FirstCleanCard({ editor, readOnly }: { editor: QuoteEditorApi; readOnly: boolean }) {
  const { t } = useTranslation();
  // Which extra the select shows. A set amount still at zero reads the same
  // as no extra in the stored price, so the choice is remembered here.
  const [chosenKind, setChosenKind] = useState<'none' | 'fixed' | 'percent' | null>(null);
  const q = editor.quote;
  if (!q) return null;
  const accepted = q.acceptedFirstClean ?? null;
  // Alternatives built from separate quotes, from before the first clean price.
  const alternatives = !accepted && q.firstClean && !q.firstClean.areaFirstClean ? q.firstClean : null;
  if (q.billingBasis !== 'per_visit' && !accepted && !alternatives) return null;
  const price = quoteFirstCleanPrice(q);
  const shownKind =
    chosenKind ?? (!price ? 'none' : price.kind === 'percent' ? 'percent' : price.value > 0 ? 'fixed' : 'none');
  const money = (cents: number) => formatCents(cents, q.currencyCode, getDateLocale());

  function setPrice(next: FirstCleanPrice | null) {
    editor.update((draft) => withFirstCleanPrice(draft, next));
  }

  const regularTasks = q.rooms.some((room) =>
    room.services.some((task) => task.isSelected && !(price && task.firstVisitOnly)),
  );
  const firstTotal = firstVisitCents(q);
  const regularTotal = editor.preview.totalCents;
  const problem = !price
    ? null
    : !q.preferredFrequency || q.preferredFrequency === 'one_time'
      ? 'oneTime'
      : q.frequencyTiersEnabled
        ? 'tiers'
        : q.pricePromise === 'range'
          ? 'range'
          : q.totals.totalOverrideCents != null
            ? 'override'
            : !regularTasks
              ? 'noTasks'
              : regularTotal <= 0
                ? 'noPrice'
                : null;

  return (
    <section className="card mb-6" aria-labelledby="first-clean-heading">
      <h2 id="first-clean-heading" className="text-lg font-semibold">
        {t('firstClean.heading')}
      </h2>
      {accepted ? (
        <>
          <p>
            {t('firstClean.approved')}: {accepted.name}
          </p>
          <ScopeOption option={accepted} currency={q.currencyCode} />
        </>
      ) : (
        <>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('firstClean.hint')}</p>
          {q.billingBasis === 'per_visit' && (
            <Checkbox
              label={t('firstClean.toggle')}
              checked={price !== null}
              disabled={readOnly}
              onChange={(event) => {
                setChosenKind(null);
                setPrice(event.target.checked ? { kind: 'fixed', value: 0 } : null);
              }}
            />
          )}
          {price && (
            <div className="mt-2">
              <Select
                label={t('firstClean.extraLabel')}
                value={shownKind}
                disabled={readOnly}
                options={[
                  { value: 'none', label: t('firstClean.extraNone') },
                  { value: 'fixed', label: t('firstClean.extraFixed') },
                  { value: 'percent', label: t('firstClean.extraPercent') },
                ]}
                onChange={(event) => {
                  const kind =
                    event.target.value === 'fixed' || event.target.value === 'percent' ? event.target.value : 'none';
                  setChosenKind(kind);
                  setPrice({ kind: kind === 'percent' ? 'percent' : 'fixed', value: 0 });
                }}
              />
              {shownKind === 'fixed' && price.kind === 'fixed' && (
                <MoneyInput
                  label={t('firstClean.extraAmount')}
                  currencyCode={q.currencyCode}
                  valueCents={price.value}
                  min={0}
                  disabled={readOnly}
                  onChangeCents={(value) => setPrice({ kind: 'fixed', value: value ?? 0 })}
                />
              )}
              {shownKind === 'percent' && price.kind === 'percent' && (
                <NumberInput
                  label={t('firstClean.extraPercentValue')}
                  value={price.value / 100}
                  min={0}
                  max={1000}
                  disabled={readOnly}
                  onValueChange={(value) => setPrice({ kind: 'percent', value: Math.round((value ?? 0) * 100) })}
                />
              )}
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('firstClean.tasksHint')}</p>
              {problem ? (
                <Alert tone="warning">{t(`firstClean.blocked.${problem}`)}</Alert>
              ) : firstTotal !== null ? (
                <p className="mt-2 font-semibold" aria-live="polite">
                  {firstTotal === regularTotal
                    ? t('firstClean.sameAsRegular')
                    : t('firstClean.summary', { first: money(firstTotal), regular: money(regularTotal) })}
                </p>
              ) : null}
            </div>
          )}
          {alternatives && (
            <>
              <h3 className="mt-4 font-semibold">{t('firstClean.legacyHeading')}</h3>
              <p className="text-sm text-[color:var(--color-muted)]">{t('firstClean.legacyNote')}</p>
              {!alternatives.reviewed && <Alert tone="warning">{t('firstClean.reviewAgain')}</Alert>}
              {alternatives.options.map((option) => (
                <ScopeOption key={option.id} option={option} currency={q.currencyCode} />
              ))}
            </>
          )}
        </>
      )}
    </section>
  );
}
